import { useState, useEffect, useRef } from 'react'
import { motion } from 'motion/react'
import { FADE, colors, accent } from '@/lib/tokens'
import { VideoLightbox } from '@/components/VideoLightbox'

const kw = '#7c3aed'  // keyword
const str = '#16a34a' // string
const fn = '#0284c7'  // function/property
const cm = '#78716c'  // comment
const pu = '#a8a29e'  // punctuation
const br = '#F26B1D'  // brand / output
const tx = '#1c1917'  // default text

type Token = { text: string; color: string }
const CODE_LINES: Token[][] = [
  [{ text: 'import', color: kw }, { text: ' { ', color: pu }, { text: 'init', color: fn }, { text: ' } ', color: pu }, { text: 'from', color: kw }, { text: ' ', color: tx }, { text: '"@recost-dev/node"', color: str }],
  [],
  [{ text: '// Call once at startup \u2014 patches fetch/http automatically', color: cm }],
  [{ text: 'init', color: br }, { text: '({', color: pu }],
  [{ text: '  ', color: tx }, { text: 'apiKey', color: fn }, { text: ': ', color: pu }, { text: 'process', color: tx }, { text: '.env', color: fn }, { text: '.RECOST_API_KEY', color: fn }, { text: ',', color: pu }],
  [{ text: '  ', color: tx }, { text: 'projectId', color: fn }, { text: ': ', color: pu }, { text: 'process', color: tx }, { text: '.env', color: fn }, { text: '.RECOST_PROJECT_ID', color: fn }, { text: ',', color: pu }],
  [{ text: '})', color: pu }],
  [],
  [{ text: '// Your existing code stays exactly the same', color: cm }],
  [{ text: 'const', color: kw }, { text: ' res = ', color: tx }, { text: 'await', color: kw }, { text: ' openai', color: tx }, { text: '.chat.completions.create', color: fn }, { text: '({', color: pu }],
  [{ text: '  ', color: tx }, { text: 'model', color: fn }, { text: ': ', color: pu }, { text: '"gpt-4o"', color: str }, { text: ',', color: pu }],
  [{ text: '  ', color: tx }, { text: 'messages', color: fn }, { text: ': ', color: pu }, { text: '[...]', color: pu }],
  [{ text: '})', color: pu }],
  [],
  [{ text: '// ', color: cm }, { text: 'recost', color: br }, { text: ': $0.0023 \u00b7 1,847 tokens \u00b7 340ms', color: br }],
]

function CodePreview() {
  const [visibleLines, setVisibleLines] = useState(0)

  useEffect(() => {
    if (visibleLines >= CODE_LINES.length) return
    const delay = CODE_LINES[visibleLines].length === 0 ? 80 : 120
    const t = setTimeout(() => setVisibleLines((n) => n + 1), delay)
    return () => clearTimeout(t)
  }, [visibleLines])

  return (
    <div
      className="code-preview-block no-scrollbar"
      style={{
        background: colors.bgBase,
        border: `1px solid ${colors.borderDefault}`,
        borderRadius: 10,
        padding: '20px 24px',
        fontFamily: 'var(--font-mono)',
        fontSize: 13,
        lineHeight: 1.7,
        boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
        height: 398,
        overflowX: 'auto',
        overflowY: 'hidden',
      }}
    >
      {/* Window chrome */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 16, position: 'sticky', left: 0 }}>
        <div style={{ width: 10, height: 10, borderRadius: '50%', background: colors.borderDefault }} />
        <div style={{ width: 10, height: 10, borderRadius: '50%', background: colors.borderDefault }} />
        <div style={{ width: 10, height: 10, borderRadius: '50%', background: colors.borderDefault }} />
      </div>

      {CODE_LINES.slice(0, visibleLines).map((line, i) => (
        <div key={i} style={{ minHeight: '1.7em', whiteSpace: 'nowrap' }}>
          {line.length > 0
            ? line.map((tok, j) => <span key={j} style={{ color: tok.color }}>{tok.text}</span>)
            : '\u00A0'
          }
        </div>
      ))}

      {visibleLines < CODE_LINES.length && (
        <span
          style={{
            display: 'inline-block',
            width: 2,
            height: '1em',
            background: accent,
            verticalAlign: 'middle',
            marginLeft: 1,
          }}
          className="cursor-blink"
        />
      )}
    </div>
  )
}

export function HeroSection() {
  const watchBtnRef = useRef<HTMLButtonElement>(null)
  const [lightboxOpen, setLightboxOpen] = useState(false)

  return (
    <section
      className="hero-section"
      style={{
        position: 'relative',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        overflow: 'visible',
        backgroundColor: colors.bgPage,
      }}
    >
      <HeroWaveBackground />
      <TealWaveBackground />

      <div
        style={{
          position: 'relative',
          zIndex: 1,
          maxWidth: 1500,
          margin: '0 auto',
          padding: '12px 24px 56px',
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1.08fr) minmax(0, 0.92fr)',
          gap: 84,
          alignItems: 'center',
          width: '100%',
          transform: 'translateY(-44px)',
        }}
        className="hero-grid"
      >
        {/* Left: copy */}
        <div>
          {/* Headline */}
          <motion.h1
            {...FADE(0.08)}
            style={{
              fontSize: 'clamp(2rem, 8vw, 4rem)',
              fontWeight: 700,
              lineHeight: 1.1,
              letterSpacing: '-0.02em',
              color: colors.textPrimary,
              margin: '0 0 20px',
            }}
          >
            Know exactly what your{' '}
            <br />
            <span style={{ color: accent }}>API costs</span>
          </motion.h1>

          {/* Subtext */}
          <motion.p
            {...FADE(0.2)}
            className="hero-subtext"
            style={{
              fontSize: 17,
              lineHeight: 1.65,
              color: colors.textSecondary,
              margin: '0 0 36px',
              maxWidth: 460,
            }}
          >
            Recost tracks every API call across your codebase. Real-time cost
            breakdowns, static analysis, and provider-level insights. Drop in
            the SDK and start seeing where your budget actually goes.
          </motion.p>

          {/* CTAs */}
          <motion.div
            {...FADE(0.25)}
            className="hero-ctas"
            style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}
          >
            <button
              ref={watchBtnRef}
              type="button"
              onClick={() => setLightboxOpen(true)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 9,
                fontSize: 16.5,
                fontWeight: 600,
                color: colors.bgBase,
                padding: '13px 26px',
                borderRadius: 8,
                background: accent,
                cursor: 'pointer',
                transition: 'opacity 0.15s, box-shadow 0.15s',
                letterSpacing: '-0.01em',
                border: 'none',
                boxShadow: '0 8px 24px -8px rgba(242, 107, 29, 0.45)',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.88')}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
            >
              <svg
                width="12"
                height="13"
                viewBox="0 0 11 12"
                fill="currentColor"
                aria-hidden="true"
                style={{ flexShrink: 0 }}
              >
                <path d="M1 1.2v9.6a.5.5 0 0 0 .76.43l8-4.8a.5.5 0 0 0 0-.86l-8-4.8A.5.5 0 0 0 1 1.2Z" />
              </svg>
              Watch&nbsp; (30s)
            </button>
            <a
              href="/docs"
              style={{
                fontSize: 15,
                fontWeight: 500,
                color: colors.textSecondary,
                textDecoration: 'none',
                padding: '12px 20px',
                borderRadius: 8,
                border: `1px solid ${colors.borderDefault}`,
                background: colors.bgBase,
                transition: 'border-color 0.15s, color 0.15s',
                letterSpacing: '-0.01em',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = colors.borderHover
                e.currentTarget.style.color = colors.textPrimary
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = colors.borderDefault
                e.currentTarget.style.color = colors.textSecondary
              }}
            >
              View docs
            </a>
          </motion.div>
        </div>

        {/* Right: code preview + supported APIs */}
        <motion.div {...FADE(0.18)} style={{ minWidth: 0, overflow: 'hidden' }}>
          <CodePreview />
          <div style={{ marginTop: 16 }}>
            <p style={{ fontSize: 12, color: colors.textMuted, marginBottom: 10, letterSpacing: '0.02em' }}>
              Works with APIs you already use
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              {['OpenAI', 'Stripe', 'Twilio', 'Google Maps', 'Anthropic'].map((name) => (
                <span
                  key={name}
                  style={{
                    fontSize: 12,
                    fontWeight: 500,
                    color: colors.textSecondary,
                    background: colors.bgBase,
                    border: `1px solid ${colors.borderDefault}`,
                    padding: '5px 11px',
                    borderRadius: 6,
                  }}
                >
                  {name}
                </span>
              ))}
            </div>
          </div>
        </motion.div>
      </div>

      <VideoLightbox
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        src="https://assets.recost.dev/videos/recost-intro.mp4"
        poster="/recost-intro-poster.jpg"
        returnFocusRef={watchBtnRef}
      />

      <style>{`
        @media (max-width: 768px) {
          .hero-section {
            align-items: flex-start !important;
            min-height: 100svh !important;
          }
          .hero-grid {
            grid-template-columns: 1fr !important;
            gap: 32px !important;
            transform: none !important;
            padding: 96px 20px 64px !important;
          }
          .code-preview-block {
            height: 380px !important;
            padding: 14px 16px 18px !important;
            font-size: 12px !important;
            max-width: 100% !important;
            box-sizing: border-box !important;
            overflow-x: auto !important;
            white-space: pre !important;
            word-break: normal !important;
          }
          .hero-ctas {
            flex-direction: row !important;
            align-items: stretch !important;
            flex-wrap: nowrap !important;
            gap: 10px !important;
            margin-top: clamp(16px, 3vw, 32px) !important;
          }
          .hero-ctas > button,
          .hero-ctas > a {
            flex: 1 1 0 !important;
            min-width: 0 !important;
            display: inline-flex !important;
            align-items: center !important;
            justify-content: center !important;
            box-sizing: border-box !important;
            font-size: 13.5px !important;
            font-weight: 600 !important;
            height: 44px !important;
            padding: 0 12px !important;
            line-height: 1 !important;
            letter-spacing: -0.01em !important;
          }
          .hero-ctas > button {
            box-shadow: 0 4px 12px -6px rgba(242, 107, 29, 0.4) !important;
            gap: 7px !important;
          }
          .hero-ctas > button svg {
            width: 11px !important;
            height: 12px !important;
          }
          .hero-subtext {
            font-size: 15.5px !important;
            line-height: 1.6 !important;
            margin-bottom: 24px !important;
          }
        }
      `}</style>
    </section>
  )
}

export function HeroWaveBackground() {
  return (
    <svg
      aria-hidden="true"
      className="pointer-events-none absolute left-0 top-[-240px] h-[calc(100%+480px)] w-full"
      viewBox="0 0 2600 760"
      preserveAspectRatio="none"
      style={{ overflow: 'visible', zIndex: 0, opacity: 0.95 }}
    >
      <defs>
        <linearGradient id="hero-wave-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#F26B1D" stopOpacity="0" />
          <stop offset="25%" stopColor="#F26B1D" stopOpacity="0.28" />
          <stop offset="50%" stopColor="#fb923c" stopOpacity="0.40" />
          <stop offset="75%" stopColor="#F26B1D" stopOpacity="0.28" />
          <stop offset="100%" stopColor="#F26B1D" stopOpacity="0" />
        </linearGradient>
        <linearGradient id="hero-wave-gradient-soft" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#F26B1D" stopOpacity="0" />
          <stop offset="50%" stopColor="#F26B1D" stopOpacity="0.18" />
          <stop offset="100%" stopColor="#F26B1D" stopOpacity="0" />
        </linearGradient>
        <filter
          id="hero-wave-blur"
          filterUnits="userSpaceOnUse"
          x="-500"
          y="-300"
          width="3600"
          height="1600"
        >
          <feGaussianBlur stdDeviation="18" />
        </filter>
      </defs>

      <g filter="url(#hero-wave-blur)" opacity="0.48">
        <path
          d="M-620,435 C220,195 760,615 1300,435 C1760,205 2260,485 3040,465"
          stroke="url(#hero-wave-gradient)"
          strokeWidth="260"
          fill="none"
          strokeLinecap="round"
        />
        <path
          d="M-640,455 C200,225 760,635 1300,455 C1760,235 2260,505 3060,485"
          stroke="url(#hero-wave-gradient-soft)"
          strokeWidth="260"
          fill="none"
          strokeLinecap="round"
          opacity="0.42"
        />
      </g>
    </svg>
  )
}

export function TealWaveBackground() {
  return (
    <svg
      aria-hidden="true"
      className="pointer-events-none absolute left-0 top-[-240px] h-[calc(100%+480px)] w-full"
      viewBox="0 0 2600 1100"
      preserveAspectRatio="none"
      style={{ overflow: 'visible', zIndex: 0, opacity: 0.62 }}
    >
      <defs>
        <linearGradient id="hero-teal-gradient-main" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#14b8a6" stopOpacity="0" />
          <stop offset="30%" stopColor="#14b8a6" stopOpacity="0.16" />
          <stop offset="55%" stopColor="#2dd4bf" stopOpacity="0.38" />
          <stop offset="80%" stopColor="#14b8a6" stopOpacity="0.16" />
          <stop offset="100%" stopColor="#14b8a6" stopOpacity="0" />
        </linearGradient>
        <linearGradient id="hero-teal-gradient-soft" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#14b8a6" stopOpacity="0" />
          <stop offset="50%" stopColor="#2dd4bf" stopOpacity="0.22" />
          <stop offset="100%" stopColor="#14b8a6" stopOpacity="0" />
        </linearGradient>
        <filter
          id="hero-teal-blur"
          filterUnits="userSpaceOnUse"
          x="-500"
          y="-300"
          width="3600"
          height="2000"
        >
          <feGaussianBlur stdDeviation="14" />
        </filter>
      </defs>

      <g filter="url(#hero-teal-blur)" opacity="0.66">
        <path
          d="M-360,605 C120,535 360,715 740,625 C1120,535 1340,435 1660,515 C1920,580 2080,695 2820,635"
          stroke="url(#hero-teal-gradient-main)"
          strokeWidth="140"
          fill="none"
          strokeLinecap="round"
        />
        <path
          d="M-300,665 C80,605 340,765 700,695 C1040,625 1260,565 1580,625 C1840,675 2040,765 2780,715"
          stroke="url(#hero-teal-gradient-soft)"
          strokeWidth="100"
          fill="none"
          strokeLinecap="round"
          opacity="0.54"
        />
      </g>
    </svg>
  )
}





